'use client';

// Third Party Plugins
import React, { useState, useEffect, useMemo } from 'react';
// Types
import { ClientSideNewsWrapperProps, NewsPost } from '../../types';
// Custom Components
import SearchForm from '../SearchForm';
import NewsList from '../NewsList';
import ImageContainer from '@/components/images/ImageContainer';
import LayoutContainer from '@/components/ui/LayoutContainer';
import ContentBlock from '@/components/blocks/ContentBlock';
import Logo from '@/components/images/Logo';
import HeroHeading from '@/components/ui/HeroHeading';
import Gallery from '@/components/images/Gallery';

const ClientSideNewsWrapper: React.FC<ClientSideNewsWrapperProps> = ({
  headingOne,
  subheading,
  pageImageBlock,
  allNews,
  hasGallery,
  galleryManyBlock,
}) => {
  // Memoize transformedAllNews to prevent unnecessary recalculations
  const transformedAllNews = useMemo(() => {
    return allNews.map((post: NewsPost) => ({
      ...post,
      _type: 'newsPost', // Add the required _type property
    }));
  }, [allNews]);

  const [searchTerm, setSearchTerm] = useState('');
  const [newsPosts, setNewsPosts] = useState<NewsPost[]>(transformedAllNews);

  useEffect(() => {
    const searchNews = () => {
      if (!searchTerm.trim()) {
        setNewsPosts(transformedAllNews);
        return;
      }
      const items = transformedAllNews.filter((post) =>
        post.newsPostTitle
          .trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase()),
      );
      setNewsPosts(items || []);
    };

    searchNews();
  }, [searchTerm, transformedAllNews]);

  return (
    <>
      <HeroHeading>
        <Logo />
        <h1>{headingOne}</h1>
        <p>{subheading}</p>
        {pageImageBlock ? (
          <ImageContainer
            image={pageImageBlock.asset.url}
            width={751}
            height={194}
            type="dynamic"
            alt={pageImageBlock.alt || ''}
          />
        ) : null}
        <SearchForm searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      </HeroHeading>
      <LayoutContainer hasSectionGaps>
        <NewsList newsPosts={newsPosts} />
        {hasGallery ? (
          <Gallery
            images={galleryManyBlock.map((image) => ({
              alt: image.alt || '',
              url: image.asset?.url || '', // Extract the URL to the top level
              asset: image.asset, // Retain the original asset structure
              crop: image.crop, // Include optional crop if available
              hotspot: image.hotspot, // Include optional hotspot if available
            }))}
          />
        ) : null}
        <ContentBlock
          showCallToAction={true}
          callToActionLink="/contact?source=add-news"
          callToActionType="internal"
          callToActionTitle="Suggest a News"
        >
          <div style={{ fontWeight: `bold`, fontSize: `18px` }}>
            If you have a news article you would like to suggest please click on
            the button below:
          </div>
        </ContentBlock>
      </LayoutContainer>
    </>
  );
};

export default ClientSideNewsWrapper;
